// Archivo: contract-manager/client/pages/access-control/index.jsx
import { useState, useEffect, useRef } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Button, Spinner, Menu, MenuHandler, MenuList, MenuItem } from '@material-tailwind/react'
import { HasAccess } from '@permify/react-role'
import { useAuthorization } from '@/hooks'
import { UserServices } from '@/services/accesscontrol-services'
import { AccessForm } from './access-form'
import { AccessResult } from '@/shared/components/access-result'
import { OwnOfficeSelector } from '@/shared/selectors/own-office-selector'
import { PageLayout } from '@/layouts/page-layout'
import { FiShield, FiSettings, FiList, FiChevronDown } from 'react-icons/fi'
import { AnyAccess } from '@/shared/components/any-access'
import { formatDocumentSync } from './gun-mode'
import { useGunControls } from './use-gun-controls'

export const AccessControl = () => {
  const { hasAccess } = useAuthorization({ redirection: { enable: false } })
  const navigate = useNavigate()
  const userServices = new UserServices()

  const [canAccess, setCanAccess] = useState(null)
  const [offices, setOffices] = useState([])
  const [officeId, setOfficeId] = useState(null)
  const [documento, setDocumento] = useState('')
  const [resultado, setResultado] = useState(null)
  const [loading, setLoading] = useState(false)
  const [isOfficePreselected, setIsOfficePreselected] = useState(false)
  const [showObservations, setShowObservations] = useState(false)
  const [observations, setObservations] = useState('')
  const [enterEvent, setEnterEvent] = useState('entry')
  const formRef = useRef(null)
  const resultRef = useRef(null)
  const [showBackToTop, setShowBackToTop] = useState(false)

  // marcador “vino de pistola”
  const lastInputWasGun = useRef(false)

  // ⚡ sumidero off-screen para asegurar foco cuando el lector no envía Enter
  const scanSinkRef = useRef(null)

  useEffect(() => {
    hasAccess('access#activate')
      .then(setCanAccess)
      .catch(() => setCanAccess(false))
  }, [])

  useEffect(() => {
    if (canAccess) {
      userServices.getOffices()
        .then((offices) => {
          setOffices(offices)
          if (offices.length === 1) {
            setOfficeId(offices[0].id)
            setIsOfficePreselected(true)
          }
        })
        .catch(() => toast.error('No se pudieron cargar las dependencias'))
    }
  }, [canAccess])

  const handleBlur = () => setDocumento(formatDocumentSync(documento))

  const clearObsAndResult = () => {
    if (resultado) {
      setResultado(null)
      setShowObservations(false)
      setObservations('')
    }
  }

  const handleClear = () => {
    setDocumento('')
    clearObsAndResult()
    lastInputWasGun.current = false
  }

  const handleChange = (e) => {
    setDocumento(e.target.value)
    clearObsAndResult()
    // tecleo manual invalida el origen pistola
    lastInputWasGun.current = false
  }

  const canAutoSubmitNow = isOfficePreselected || (officeId && officeId !== 'all')

  // submit con overrides y meta
  const handleSubmit = async (e, event = 'entry', documentOverride = null, opts = {}) => {
    if (e && typeof e.preventDefault === 'function') e.preventDefault()

    const source = (documentOverride ?? documento).trim()
    const formatted = formatDocumentSync(source)
    const cleaned = formatted.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
    if (!cleaned) return toast.error('Ingrese un documento válido')
    if ((event === 'entry' || event === 'exit') && !canAutoSubmitNow) {
      return toast.error('Seleccione una dependencia para registrar entrada o salida')
    }

    const pistol = typeof opts.pistol === 'boolean' ? opts.pistol : lastInputWasGun.current

    setDocumento(formatted)
    setLoading(true)

    try {
      const response = await userServices.verifyAccess(cleaned, officeId, event, observations, { pistol })
      setResultado({
        access: response.access,
        message: response.access
          ? (event === 'exit' ? 'Salida registrada' : 'Autorizado')
          : 'No Autorizado',
        tipo: response.type,
        data: response.data,
        detail: response.detail || null,
        observations: response.observations || null,
        displayDoc: formatted
      })

      if (window.innerWidth < 768 && resultRef.current) {
        setTimeout(() => {
          resultRef.current.scrollIntoView({ behavior: 'smooth' })
          setShowBackToTop(true)
        }, 100)
      }
    } catch {
      setResultado({ acceso: false, mensaje: 'No Autorizado', displayDoc: formatted })
    } finally {
      setLoading(false)
      // tras cualquier envío, reseteamos el marcador
      lastInputWasGun.current = false
    }
  }

  // integra controles de pistola (usa listener global + idle)
  const {
    gunMode,
    setGunMode,
    gunDefaultEvent,
    setGunDefaultEvent,
    gunAutoSubmit,
    setGunAutoSubmit,
    showOfficeTooltip,
    blockOfficeSelectorEvents,
    handleOverlayClick
  } = useGunControls({
    canAutoSubmitNow,
    onInsert: (formattedRut) => {
      lastInputWasGun.current = true
      setDocumento('')
      clearObsAndResult()
      requestAnimationFrame(() => setDocumento(formattedRut))
    },
    onSubmit: (formattedRut, event) => {
      // autosubmit desde pistola
      handleSubmit({ preventDefault: () => {} }, event, formattedRut, { pistol: true })
    },
    toast
  })

  // mantener el sumidero off-screen enfocado mientras el modo pistola está activo
  useEffect(() => {
    if (!gunMode) return
    const el = scanSinkRef.current
    if (!el) return

    const tryFocus = () => {
      try { el.focus() } catch (_) {}
    }

    // foco inicial
    const t = setTimeout(tryFocus, 60)

    // re-enfocar si el usuario pincha por ahí o se pierde el foco
    const onWindowClick = () => tryFocus()
    const onBlur = () => setTimeout(tryFocus, 30)

    window.addEventListener('click', onWindowClick, true)
    el.addEventListener('blur', onBlur, true)

    return () => {
      clearTimeout(t)
      window.removeEventListener('click', onWindowClick, true)
      el.removeEventListener('blur', onBlur, true)
    }
  }, [gunMode])

  const handleLogout = async () => {
    try {
      await userServices.signOut()
      navigate('/sign_in')
    } catch {
      toast.error('Error al cerrar sesión')
    }
  }

  if (canAccess === null) {
    return (
      <div className="h-screen flex items-center justify-center">
        <Spinner className="h-10 w-10" />
      </div>
    )
  }
  if (!canAccess) { navigate('/dashboard'); return null }

  return (
    <PageLayout
      title="Control de Acceso"
      actions={
        <div key="asset-action-buttons" className="flex gap-3">
          <AnyAccess permissions={['access#history', 'access#except', 'access#settings']}>
            <Menu placement="bottom-end">
              <MenuHandler>
                <Button variant="outlined" className="flex items-center gap-2">
                  Acciones
                  <FiChevronDown className="h-4 w-4" />
                </Button>
              </MenuHandler>
              <MenuList className="p-1 min-w-[220px]">
                <HasAccess permissions="access#history">
                  <MenuItem className="p-2">
                    <Link to="/controldeacceso/logs" className="flex items-center gap-2 w-full">
                      <FiList className="h-4 w-4" />
                      <span>Ver registros</span>
                    </Link>
                  </MenuItem>
                </HasAccess>
                <HasAccess permissions="access#except">
                  <MenuItem className="p-2">
                    <Link to="/controldeacceso/exceptions" className="flex items-center gap-2 w-full">
                      <FiShield className="h-4 w-4" />
                      <span>Excepciones</span>
                    </Link>
                  </MenuItem>
                </HasAccess>
                <HasAccess permissions="access#settings">
                  <MenuItem className="p-2">
                    <Link to="settings" className="flex items-center gap-2 w-full">
                      <FiSettings className="h-4 w-4" />
                      <span>Configuración</span>
                    </Link>
                  </MenuItem>
                </HasAccess>
              </MenuList>
            </Menu>
          </AnyAccess>
        </div>
      }
      className="flex flex-col gap-6 h-full"
    >
      <div
        className="relative group"
        {...blockOfficeSelectorEvents}
        aria-describedby={gunMode ? 'office-tip' : undefined}
      >
        <OwnOfficeSelector offices={offices} officeId={officeId} setOfficeId={setOfficeId} />
        {gunMode && (
          <div
            className="absolute inset-0 z-10 bg-transparent cursor-not-allowed"
            aria-hidden="true"
            onClick={handleOverlayClick}
          />
        )}
        {gunMode && showOfficeTooltip && (
          <div
            id="office-tip"
            role="tooltip"
            className="absolute top-full left-1/2 -translate-x-1/2 mt-2 z-20 rounded-md bg-gray-900 text-white text-xs px-2 py-1 shadow"
          >
            No se puede cambiar de dependencia en Modo pistola
          </div>
        )}
      </div>

      {/* Sumidero off-screen para captar teclas del lector en PC sin Enter */}
      <div
        ref={scanSinkRef}
        contentEditable
        tabIndex={gunMode ? 0 : -1}
        aria-hidden={!gunMode}
        className={`${gunMode ? '' : 'hidden'}`}
        style={{
          position: 'fixed',
          top: '-9999px',
          left: '-9999px',
          width: '1px',
          height: '1px',
          opacity: 0,
          pointerEvents: 'none',
          userSelect: 'none',
          outline: 'none'
        }}
        role="textbox"
        suppressContentEditableWarning={true}
      />

      <main className="flex flex-1 flex-col md:flex-row overflow-auto w-full">
        <div ref={formRef} className="w-full md:w-1/3 p-4 bg-white">
          <div className="space-y-6">
            <AccessForm
              documento={documento}
              setDocumento={setDocumento}
              handleChange={handleChange}
              handleBlur={handleBlur}
              handleClear={handleClear}
              handleSubmit={handleSubmit}
              loading={loading}
              officeId={officeId}
              isOfficePreselected={isOfficePreselected}
              showObservations={showObservations}
              setShowObservations={setShowObservations}
              observations={observations}
              setObservations={setObservations}
              gunMode={gunMode}
              setGunMode={setGunMode}
              gunDefaultEvent={gunDefaultEvent}
              setGunDefaultEvent={setGunDefaultEvent}
              gunAutoSubmit={gunAutoSubmit}
              setGunAutoSubmit={setGunAutoSubmit}
              canAutoSubmitNow={canAutoSubmitNow}
              enterEvent={enterEvent}
              setEnterEvent={setEnterEvent}
            />
          </div>
        </div>

        <div ref={resultRef} className="w-full md:flex-1 p-1 bg-gray-50">
          <AccessResult result={resultado} />
        </div>
      </main>

      {showBackToTop && (
        <div className="fixed bottom-20 right-4 z-50 md:hidden">
          <Button
            size="sm"
            onClick={() => {
              if (formRef.current) {
                formRef.current.scrollIntoView({ behavior: 'smooth' })
                setShowBackToTop(false)
              }
            }}
            className="bg-blue-600 text-white"
          >
            Volver arriba
          </Button>
        </div>
      )}

      <footer className="px-4 py-2 bg-white shadow">
        <Button onClick={handleLogout} variant="text" color="red" className="w-full">
          <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
            <path d="M16 17v-3H9v-4h7V7l5 5-5 5zM14 2a2 2 0 0 1 2 2v2h-2V4H5v16h9v-2h2v2a2 2 0 0 1-2 2H5z" />
          </svg>
          Cerrar sesión
        </Button>
      </footer>
    </PageLayout>
  )
}
